import { useEffect, useState } from "react";

// Debounce a value. In `onlyNew` mode the effect keys off a serialized snapshot
// instead of the raw value, so callers that hand in a fresh object reference on
// every render (e.g. RHF `watch()`) don't reset the timer unless the content
// actually changed — otherwise unrelated re-renders during the delay window keep
// cancelling the pending timer and the debounced value never settles.
export const useDebouncedValue = <T>(value: T, delay: number = 500, onlyNew = false) => {
    const [debouncedValue, setDebouncedValue] = useState<T>(value);

    // stable across re-renders that don't change the content (only in onlyNew mode)
    const key = onlyNew ? JSON.stringify(value) : value;

    useEffect(() => {
        // only content changes (key) re-run this effect and reset the timer; the
        // closed-over `value` matches the current key, so it's the latest content
        const timeoutId = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(timeoutId);
        };
    }, [key, delay]);

    return debouncedValue;
};
